/**
 * Dashboard "Show" filter (table and calendar): which stages of a show to list.
 * Mirrors backend events.lifecycle-filter.ts (`lifecycle` query param of
 * GET /getAllEvents). Cancelled stays in the status filter.
 */
export type EventType = "draft" | "newRequest" | "pendingApproval" | "approved";

export const EVENT_TYPE_OPTIONS: { value: EventType; label: string; badge: string; hint: string }[] = [
  { value: "draft", label: "Drafts", badge: "Draft", hint: "Started but never submitted" },
  { value: "newRequest", label: "New requests", badge: "New request", hint: "Submitted, estimate not sent yet" },
  { value: "pendingApproval", label: "Estimates sent", badge: "Estimate sent", hint: "Estimate sent, waiting for approval" },
  { value: "approved", label: "Approved", badge: "Approved", hint: "Approved shows" },
];

const ALL: EventType[] = EVENT_TYPE_OPTIONS.map((o) => o.value);

/**
 * Default = everything, drafts included (Osh/Casper/Tiff, 29/09: "the dashboard
 * needs to show everything, then we apply filters").
 */
export const DEFAULT_EVENT_TYPES: EventType[] = [...ALL];

/** Keeps known values in a stable order; empty or junk falls back to the default. */
export function sanitizeEventTypes(input: unknown): EventType[] {
  const list = Array.isArray(input) ? input : [];
  const clean = ALL.filter((t) => list.includes(t));
  return clean.length ? clean : [...DEFAULT_EVENT_TYPES];
}

export function isDefaultEventTypes(value?: EventType[] | null): boolean {
  const v = sanitizeEventTypes(value);
  return v.length === DEFAULT_EVENT_TYPES.length && DEFAULT_EVENT_TYPES.every((t) => v.includes(t));
}

export function isAllEventTypes(value?: EventType[] | null): boolean {
  return sanitizeEventTypes(value).length === ALL.length;
}

/**
 * Query param for GET /getAllEvents. Always sent: without it the API keeps its
 * historical list (submitted shows only, no drafts).
 */
export function eventTypesToParams(value?: EventType[] | null): { lifecycle: string } {
  return { lifecycle: sanitizeEventTypes(value).join(",") };
}

/** Singular tag for one row ("Draft", "Estimate sent"). */
export function eventTypeBadgeLabel(value?: string | null): string | null {
  return EVENT_TYPE_OPTIONS.find((o) => o.value === value)?.badge ?? null;
}

export { ALL as ALL_EVENT_TYPES };
